<template>
  <div>
    <p class="default-locations-subtitle">
      {{ $t('defaultLocations.subtitle') }}
    </p>

    <DefaultLocationsSearch @handle-locations-list-update="getLocations" />

    <div
      v-if="forms.defaultLocations.analysisId"
      class="analysis-detail"
    >
      <h2 class="analysis-detail__header">
        {{ forms.defaultLocations.code }}
      </h2>
      <span class="analysis-detail__description">
        {{ forms.defaultLocations.description }},
        {{ forms.defaultLocations.method }}
      </span>
      <span class="analysis-detail__instrument">
        {{ forms.defaultLocations.instrument }}
      </span>
    </div>

    <div v-if="forms.defaultLocations.analysisId">
      <TableWrapper
        :table-columns="defaultLocationsColumns"
        table-key="defaultLocations"
        @editLocation="openEditModal"
        @deleteLocation="deleteLocation"
      />

      <ViewActionButtons :buttons="actionButtons" />

      <CreateEditDefaultLocation
        v-if="openCreateEditLocation"
        :is-modal-edit="isModalEdit"
        :initial-data="initialAnalysesData"
        :analysis="{
          id: forms.defaultLocations.analysisId,
          code: forms.defaultLocations.code,
          description: forms.defaultLocations.description,
          method: forms.defaultLocations.method,
          instrument: forms.defaultLocations.instrument,
        }"
        @dialogClose="handleDialogClose"
      />
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import { defaultLocationsColumns } from '@/config';
  import TableWrapper from '@/components/TableWrapper.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import { analysesApi } from '@/api';

  import DefaultLocationsSearch from './DefaultLocationsSearch.vue';
  import CreateEditDefaultLocation from './CreateEditDefaultLocation.vue';

  export default {
    name: 'DefaultLocationsIndex',
    components: {
      TableWrapper,
      ViewActionButtons,
      DefaultLocationsSearch,
      CreateEditDefaultLocation,
    },
    data() {
      return {
        defaultLocationsColumns,
        initialAnalysesData: {},
        openCreateEditLocation: false,
        isModalEdit: false,
        actionButtons: [
          {
            text: this.$t('defaultLocations.create.createLocation'),
            type: 'primary',
            permissions: ['analysisLocationsManage'],
            action: () => {
              this.openCreateEditLocation = true;
            },
          },
        ],
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'forms']),
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'resetTable',
        'parseQueryStringStatus',
      ]),
      async getLocations() {
        const analysisId = this.forms.defaultLocations.analysisId || null;
        if (analysisId) {
          try {
            const defaultLocationsIndex =
              await analysesApi.defaultLocationsIndex(
                analysisId,
                this.tableFilters('defaultLocations')
              );
            const defaultLocationsCount =
              await analysesApi.defaultLocationsCount(analysisId);
            this.updateTableRows({
              tableKey: 'defaultLocations',
              rows: defaultLocationsIndex.data,
            });
            this.updateTableCount({
              tableKey: 'defaultLocations',
              count: defaultLocationsCount.data.count,
            });
          } catch (error) {
            this.$notify({
              showClose: true,
              type: 'error',
              message: error.response.data.error.message,
            });
          }
        } else {
          this.resetTable('defaultLocations');
        }
      },
      handleDialogClose(status) {
        this.openCreateEditLocation = false;
        if (status.success) this.getLocations();
        if (this.isModalEdit) {
          this.isModalEdit = false;
        }
      },
      openEditModal(row) {
        this.initialAnalysesData = row;
        this.isModalEdit = true;
        this.openCreateEditLocation = true;
      },
      deleteLocation(row) {
        this.$confirm(
          this.$t('global.confirmAction'),
          this.$t('global.warning'),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
          }
        ).then(async () => {
          await analysesApi.defaultLocationsDelete(row.id);
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('locationsManagement.create.success'),
          });
          this.getLocations();
        });
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.getLocations();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.getLocations();
      next();
    },
  };
</script>

<style lang="scss">
  .default-locations-subtitle {
    color: $--color-white;
    padding-bottom: 1rem;
  }

  .analysis-detail {
    color: $--color-white;
    display: flex;
    flex-direction: column;
    &__header {
      padding-bottom: 0.5rem;
      font-size: 18px;
    }
    &__description {
      padding-bottom: 0.5rem;
    }
  }
</style>
